import * as React from "react";

import { cn } from "@/lib/utils";
import { useStickyHeaderOffset, useStickyFooterOffset } from "@/hooks/useStickyHeaderOffset";
import { useStickyToolbarHeight } from "@/hooks/useStickyToolbar";

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  /**
   * AUT-197: pin the header row while the page scrolls through a long table
   * (default: true -- the standard for every table, matching the prototype).
   * Matches the Lovable prototype's approach: the table stays full height and
   * the page keeps scrolling (no nested scrollbar). The table lives inside a
   * horizontally scrollable wrapper -- which counts as a scrollport in BOTH
   * axes -- so plain `position: sticky` can never reach the page scroller.
   * The offset is measured in JS and written to the header's transform.
   */
  stickyHeader?: boolean;
  /** Extra px gap between the scroll container top and the pinned header row. */
  stickyHeaderOffset?: number;
  /** AUT-317: keep the table's <tfoot> (summary/total row) pinned to the bottom while scrolling. */
  stickyFooter?: boolean;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, stickyHeader = true, stickyHeaderOffset = 0, stickyFooter = false, style, ...props }, ref) => {
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const [scrolled, setScrolled] = React.useState(false);

    const tableRef = React.useRef<HTMLTableElement | null>(null);
    const setTableRef = React.useCallback(
      (node: HTMLTableElement | null) => {
        tableRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLTableElement | null>).current = node;
      },
      [ref],
    );

    React.useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      const update = () => setScrolled(el.scrollLeft > 4);
      update();
      el.addEventListener("scroll", update, { passive: true });
      return () => el.removeEventListener("scroll", update);
    }, []);

    const theadRef = React.useRef<HTMLTableSectionElement | null>(null);
    React.useEffect(() => {
      theadRef.current = tableRef.current?.tHead ?? null;
    });

    // AUT-419: when a sticky bulk-actions bar is present (via StickyToolbarProvider), pin the header
    // just below it. Defaults to 0 with no provider — unchanged for every other table.
    const toolbarHeight = useStickyToolbarHeight();

    useStickyHeaderOffset({
      enabled: stickyHeader,
      contentRef: tableRef,
      headerRef: theadRef,
      skipRef: scrollRef,
      offset: stickyHeaderOffset + toolbarHeight,
    });

    const tfootRef = React.useRef<HTMLTableSectionElement | null>(null);
    React.useEffect(() => {
      tfootRef.current = tableRef.current?.tFoot ?? null;
    });

    useStickyFooterOffset({
      enabled: stickyFooter,
      contentRef: tableRef,
      footerRef: tfootRef,
      skipRef: scrollRef,
    });

    return (
      <div
        ref={scrollRef}
        data-scrolled={scrolled ? "true" : "false"}
        className="relative w-full overflow-auto scroll-shadow-x"
      >
        <table
          ref={setTableRef}
          style={style}
          className={cn(
            "w-full caption-bottom text-sm border-collapse",
            stickyHeader &&
              "[&>thead]:relative [&>thead]:z-30 [&>thead]:bg-card [&>thead]:will-change-transform",
            stickyFooter &&
              "[&>tfoot]:relative [&>tfoot]:z-30 [&>tfoot]:bg-card [&>tfoot]:will-change-transform",
            className,
          )}
          {...props}
        />
      </div>
    );
  },
);
Table.displayName = "Table";

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <thead ref={ref} className={cn("sticky top-0 z-20 bg-card [&_tr]:border-b [&_tr]:border-border [&_tr]:hover:bg-transparent", className)} {...props} />,
);
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tbody ref={ref} className={cn("[&_tr:last-child]:border-0 [&_tr]:border-border/50", className)} {...props} />
  ),
);
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tfoot ref={ref} className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)} {...props} />
  ),
);
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn("border-b border-transparent transition-colors data-[state=selected]:bg-muted hover:bg-muted/50", className)}
      {...props}
    />
  ),
);
TableRow.displayName = "TableRow";

/**
 * Shared sticky-column treatment for pinning cells to the left edge.
 * - Solid bg-card background so scrolling content never shows through
 * - Body row-hover and selected-state background sync
 * - Right-edge gradient shadow gated via data-scrolled on the Table wrapper
 */
const stickyColBase =
  "sticky left-0 bg-card " +
  "before:content-[''] before:pointer-events-none before:absolute before:inset-0 before:bg-surface-tint-soft " +
  "before:opacity-0 before:transition-opacity before:duration-150 [tbody_tr:hover>&]:before:opacity-100 " +
  "[tr[data-state=selected]>&]:before:opacity-100 [tr[data-state=selected]>&]:before:bg-muted " +
  "after:content-[''] after:pointer-events-none after:absolute after:top-0 after:right-0 after:bottom-0 " +
  "after:w-2 after:translate-x-full after:bg-gradient-to-r after:from-foreground/10 after:to-transparent " +
  "after:opacity-0 after:transition-opacity [[data-scrolled=true]_&]:after:opacity-100 " +
  "[&>*]:relative [&>*]:z-[1]";

interface StickyProp {
  /** Pin this cell to the left edge with sticky-column treatment (solid bg, right-edge shadow, hover sync). */
  sticky?: boolean;
}

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement> & StickyProp
>(({ className, sticky, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-12 px-4 text-left align-middle font-normal text-muted-foreground border-0 [&:has([role=checkbox])]:pr-0",
      sticky && `${stickyColBase} z-30`,
      className,
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> & StickyProp
>(({ className, sticky, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "p-4 align-middle border-0 [&:has([role=checkbox])]:pr-0",
      sticky && `${stickyColBase} z-10`,
      className,
    )}
    {...props}
  />
));
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
  ({ className, ...props }, ref) => (
    <caption ref={ref} className={cn("mt-4 text-sm text-muted-foreground", className)} {...props} />
  ),
);
TableCaption.displayName = "TableCaption";

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
